/**
 * The renderer. Assembles background + tunnel shell + hazards + hull, runs the chase camera with
 * trauma-driven shake, and interpolates between sim states using `alpha` (FS-02 R05).
 */
import * as THREE from 'three';
import { CAMERA_FOV, CAMERA_Y, CAMERA_Z, PLAY_RADIUS } from '../core/constants';
import { LIVE_HAZARD_CAP } from '../core/constants';
import type { ShipState } from '../player/flight-model';
import type { Director } from '../game/director';
import type { Resonance } from '../player/resonance';
import type { Pool } from '../core/pool';
import type { Hazard } from '../game/hazards/instance';
import type { Pickup } from '../game/pickups';
import { PALETTE } from './palette';
import { Background } from './background';
import { TunnelShell } from './tunnel';
import { Hull } from './hull';
import { HazardRenderer } from './hazard-view';
import { PickupRenderer } from './pickup-view';

/**
 * Ceiling on the FS-03 F12 pull-back. Past this the ship reads as a speck and the tunnel as a
 * corridor; an ultra-tall viewport clips the rim slightly rather than losing all sense of scale.
 */
const PORTRAIT_MAX_CAMERA_Z = 36;

export interface RenderWorld {
  ship: ShipState;
  director: Director;
  resonance: Resonance;
  hazards: Pool<Hazard>;
  pickups: Pool<Pickup>;
  playing: boolean;
  decoys: number;
  echoPilot: boolean;
}

export class View {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private tunnel = new TunnelShell();
  private background = new Background();
  private hull = new Hull();
  private hazardRenderer = new HazardRenderer(LIVE_HAZARD_CAP);
  private pickupRenderer = new PickupRenderer();
  private decoyMeshes: THREE.Mesh[] = [];
  private trauma = 0;
  /** Resting camera Z, aspect-derived (FS-03 F12). */
  private restZ = CAMERA_Z;
  private lastMs = performance.now();
  /** `camera-lean` target and strength, both cosmetic (FS-01 §5.3). */
  private leanX = 0;
  private leanY = 0;
  private leanAmount = 0;
  private smoothedLean = 0;
  private readonly reducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.scene.background = PALETTE.bg.clone();
    this.scene.fog = new THREE.Fog(PALETTE.bg.getHex(), 180, 420);

    this.camera = new THREE.PerspectiveCamera(
      CAMERA_FOV,
      window.innerWidth / window.innerHeight,
      0.1,
      700,
    );
    this.applyAspectFraming();
    this.camera.position.set(0, CAMERA_Y, this.restZ);
    this.camera.lookAt(0, 0, -30);

    this.scene.add(this.background.group);
    this.scene.add(this.tunnel.group);
    this.scene.add(this.hazardRenderer.group);
    this.scene.add(this.pickupRenderer.group);
    this.scene.add(this.hull.group);

    // Prism Split decoys.
    const decoyGeo = new THREE.OctahedronGeometry(0.4);
    for (let i = 0; i < 2; i++) {
      const m = new THREE.Mesh(
        decoyGeo,
        new THREE.MeshBasicMaterial({ color: PALETTE.secondary, transparent: true, opacity: 0.7, wireframe: true }),
      );
      m.visible = false;
      this.decoyMeshes.push(m);
      this.scene.add(m);
    }
  }

  addTrauma(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  /**
   * `camera-lean` (FS-01 §5.3) — bias the camera toward an approaching node so a new player's eye
   * is pulled to the gap before they know to look for it. Cosmetic only: it moves the camera, never
   * the ship, and never feeds simulation state (invariant 3).
   *
   * Disabled under `prefers-reduced-motion`, which §5.3 requires and which is checked here rather
   * than at the call site so no caller can forget.
   */
  setNodeLean(x: number, y: number, amount: number): void {
    this.leanX = x;
    this.leanY = y;
    this.leanAmount = this.reducedMotion ? 0 : Math.max(0, Math.min(1, amount));
  }

  clearNodeLean(): void {
    this.leanAmount = 0;
  }

  /** Re-tint the whole scene to the active sector palette (call after `applyPalette`). */
  applySectorPalette(): void {
    (this.scene.background as THREE.Color).copy(PALETTE.bg);
    (this.scene.fog as THREE.Fog).color.copy(PALETTE.bg);
    this.background.retint();
  }

  resize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.applyAspectFraming();
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  /**
   * FS-03 F12 — the only permitted aspect-dependent camera change. Vertical FOV is preserved, so a
   * tall viewport narrows the *horizontal* FOV until the tunnel rim leaves the screen; the camera
   * pulls back to restore it. That is also what gives portrait its vertical margin, which FS-12
   * §4.3.1's band layout puts the HUD into.
   *
   * F12 specifies a flat `CAM_REST_Z + 3`, which does not survive a real phone: at aspect 0.46 the
   * horizontal half-FOV is 17.2°, so covering `PLAY_RADIUS` needs z ≈ 33.8, not 17. A fixed offset
   * leaves the playfield edge — where every rim-polarity hazard lives — off screen, which is a
   * Pillar P2 failure, so the pull-back is *derived* from the aspect instead. Landscape is
   * unaffected: at 16:9 the required z is 14, exactly `CAMERA_Z`.
   */
  private applyAspectFraming(): void {
    const vHalf = (CAMERA_FOV * Math.PI) / 360;
    const hHalf = Math.atan(Math.tan(vHalf) * this.camera.aspect);
    const needed = PLAY_RADIUS / Math.tan(hHalf);
    this.restZ = Math.min(Math.max(needed, CAMERA_Z), PORTRAIT_MAX_CAMERA_Z);
  }

  render(alpha: number, world: RenderWorld): void {
    const now = performance.now();
    let dt = (now - this.lastMs) / 1000;
    this.lastMs = now;
    if (!Number.isFinite(dt) || dt < 0) dt = 0;
    if (dt > 0.1) dt = 0.1;

    const od = world.resonance.overdrive;

    // Cosmetic tunnel scroll follows the sim scroll speed.
    this.tunnel.update(world.playing ? world.director.scrollSpeed : 8, dt, od);

    // Interpolated ship.
    const s = world.ship;
    const sx = s.prevX + (s.x - s.prevX) * alpha;
    const sy = s.prevY + (s.y - s.prevY) * alpha;
    this.hull.update(sx, sy, s.bank, s.iframes, od, dt);

    // Prism Split decoys flank the ship.
    for (let i = 0; i < this.decoyMeshes.length; i++) {
      const m = this.decoyMeshes[i]!;
      m.visible = world.decoys > i;
      if (m.visible) {
        const side = i === 0 ? -1 : 1;
        m.position.set(sx + side * 1.4, sy, 0);
        m.rotation.y += dt * 2;
      }
    }

    // Hazards.
    this.hazardRenderer.sync(world.hazards.items, world.hazards.live, alpha, world.echoPilot);
    this.pickupRenderer.sync(world.pickups.items, world.pickups.live, alpha, dt, sx, sy);

    // Chase camera + trauma shake.
    this.trauma = Math.max(0, this.trauma - dt * 1.6);
    const shake = this.trauma * this.trauma;
    // `camera-lean` eases in and out rather than snapping, so the pull toward a node reads as
    // attention rather than as a cut.
    this.smoothedLean += (this.leanAmount - this.smoothedLean) * Math.min(1, dt * 3);
    const leanX = this.leanX * this.smoothedLean * 0.22;
    const leanY = this.leanY * this.smoothedLean * 0.18;

    const camX = sx * 0.28 + leanX + (Math.random() * 2 - 1) * shake * 1.4;
    const camY = CAMERA_Y + sy * 0.2 + leanY + (Math.random() * 2 - 1) * shake * 1.4;
    this.camera.position.x += (camX - this.camera.position.x) * Math.min(1, dt * 8);
    this.camera.position.y += (camY - this.camera.position.y) * Math.min(1, dt * 8);
    this.camera.position.z = this.restZ;
    this.camera.rotation.z = (Math.random() * 2 - 1) * shake * 0.05;
    this.camera.lookAt(sx * 0.35 + leanX, sy * 0.25 + leanY, -30);

    this.renderer.render(this.scene, this.camera);
  }
}
